import { useState } from 'react';
import './styles.css';
import { validateInput } from '../../utils/validators/Validators';
import { FormControl, TextField, Autocomplete, useTheme, Box, Typography } from '@mui/material';
import strings from 'common/Translation/Translate';
import Currency from 'utils/hooks/getCurrency';

/** Always returns a string — Autocomplete getOptionLabel requires it. */
const optionLabelText = option => {
  if (!option) return '';
  if (typeof option === 'string') return option;

  if (typeof option.label === 'string') {
    return option.label;
  }

  // Country / flag options pass a React element as label
  const children = option?.label?.props?.children;
  if (children != null) {
    if (Array.isArray(children)) {
      const textChild = children.find(child => typeof child?.props?.children === 'string');
      if (typeof textChild?.props?.children === 'string') {
        return textChild.props.children;
      }
    } else if (typeof children === 'string') {
      return children;
    } else if (typeof children?.props?.children === 'string') {
      return children.props.children;
    }
  }

  if (option.value != null && option.value !== '') {
    return String(option.value);
  }
  return '';
};

const hasNumeric = value => value != null && value !== '' && !Number.isNaN(Number(value));

const optionSecondaryText = option => {
  if (!option) return '';
  const minCapacity = option.minCapacity;
  const maxCapacity = option.maxCapacity;
  const pricePerPerson = option.pricePerPerson;

  if (typeof option.secondary === 'string' && option.secondary.trim()) {
    return option.secondary.trim();
  }
  if (hasNumeric(minCapacity) && hasNumeric(maxCapacity)) {
    return strings.formatString(strings.dropdown_layout_capacity, minCapacity, maxCapacity);
  }
  if (hasNumeric(maxCapacity)) {
    return strings.formatString(strings.dropdown_max_capacity, maxCapacity);
  }
  if (hasNumeric(pricePerPerson)) {
    return strings.formatString(strings.dropdown_price_per_person, Currency(pricePerPerson));
  }
  return '';
};

const valuesEqual = (a, b) => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (a === '' || b === '') return a === b;
  return String(a) === String(b);
};

const DropDownInput = ({
  hasError,
  validators,
  onChange,
  onFocus,
  field,
  inputValue,
  options,
  fieldError,
  inputProps,
  disabled,
  className,
  id,
  labelId,
  label,
  style,
  placeholder,
  sx,
  size = 'small',
  isClearable = false,
  noOptionsText = 'No options',
}) => {
  const theme = useTheme();
  const [error, setError] = useState(null);

  // Find the selected option object from inputValue (string/number safe)
  const selectedOption = options?.find(option => valuesEqual(option.value, inputValue)) || null;

  const handleChange = (event, newValue) => {
    const value = newValue ? newValue.value : '';
    const inputError = validateInput(validators, value);
    setError(inputError);
    onChange(value, field, inputError);
  };

  const customMessage = () => (!error && hasError && fieldError ? strings.requiredText : '');

  const filterOptions = (opts, { inputValue: search }) => {
    if (!search) {
      return opts;
    }

    const searchValue = String(search).toLowerCase().trim();
    return opts?.filter(option => {
      const haystack = `${optionLabelText(option)} ${optionSecondaryText(option)}`.toLowerCase();
      return haystack.includes(searchValue);
    });
  };

  // Master Data drawers use modal + 1; keep list above them (same idea as MultiSelect).
  const popperZIndex = (theme?.zIndex?.modal ?? 1300) + 2;

  return (
    <FormControl
      size={size}
      error={!!((error && error.error) || (!error && hasError && fieldError))}
      className={`dropdown ${className}`}
      fullWidth>
      <Autocomplete
        sx={sx}
        style={style}
        disabled={disabled}
        id={id}
        value={selectedOption}
        options={options || []}
        getOptionLabel={optionLabelText}
        renderOption={(props, option) => {
          const label = optionLabelText(option);
          const secondary = optionSecondaryText(option);
          return (
            <li {...props} key={option?.value ?? label}>
              <Box sx={{ py: 0.25 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                  {typeof option?.label === 'string' || option?.label?.props ? option.label : label}
                </Typography>
                {secondary ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                    {secondary}
                  </Typography>
                ) : null}
              </Box>
            </li>
          );
        }}
        isOptionEqualToValue={(option, value) => valuesEqual(option?.value, value?.value)}
        disableClearable={!isClearable}
        onChange={handleChange}
        filterOptions={filterOptions}
        noOptionsText={noOptionsText}
        componentsProps={{
          popper: {
            sx: { zIndex: popperZIndex },
          },
        }}
        onOpen={() => {
          if (onFocus) {
            onFocus(null, field);
          }
        }}
        renderInput={params => {
          return (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              InputProps={{
                ...params.InputProps,
              }}
              inputProps={{
                ...params.inputProps,
                ...(inputProps || {}),
              }}
              size={size}
              error={!!((error && error.error) || (!error && hasError && fieldError))}
              helperText={error && error.error ? error.message : customMessage()}
            />
          );
        }}
      />
    </FormControl>
  );
};
export default DropDownInput;
